import { createFileRoute, Outlet, useMatches, useRouter } from '@tanstack/react-router'
import { noop, useQueryClient } from '@tanstack/react-query'
import { lazy, Suspense, useEffect } from 'react'
import { useSessionUser } from '@/hooks/use-session'
import { getChromeVariant, UserMenuSkeleton } from '@/routes/_app/-components/app-layout-skeleton'
import { PublicShellPending } from '@/routes/_app/-components/shell-pending'
import { Guarded } from '@/routes/_app/-components/access-guard'
import { useIsEmbedded } from '@/routes/_app/-components/use-is-embedded'

/**
 * The PUBLIC shell: every page an anonymous visitor uses outside the landing — the entry screens, the emailed links,
 * the register wizards and the org survey. It draws no chrome, so none of these pages downloads the signed-in one
 * (`_app`); its guard only moves a finished user off an entry screen — docs/notes/app/app-shell.md.
 */
export const Route = createFileRoute('/_public')({
  // As under `_app`: noindex unless a route's own `pageSeo` says otherwise, so a page opts IN to being indexed.
  head: () => ({ meta: [{ name: 'robots', content: 'noindex, nofollow' }] }),
  component: PublicLayout,
  pendingComponent: PublicShellPending,
})

// Fetched only for a visitor already signed in mid-registration: the menu drags the signed-in chrome's dialogs.
const OnboardingUserMenu = lazy(() =>
  import('@/routes/_app/-components/user-menu').then((module) => ({ default: module.UserMenu })),
)

function PublicLayout() {
  const matches = useMatches()
  const routeId = matches[matches.length - 1]?.routeId ?? ''
  usePreloadAppShell()
  const { previewFrom } = Route.useRouteContext()
  if (previewFrom !== undefined) return <Outlet />
  return <Guarded shell="/_public">{getChromeVariant(routeId) === 'simple' ? <OnboardingShell /> : <Outlet />}</Guarded>
}

function OnboardingShell() {
  const user = useSessionUser()
  const embedded = useIsEmbedded()
  return (
    <>
      {user && !embedded && (
        <div className="flex justify-end px-4 pt-3 print:hidden">
          <Suspense fallback={<UserMenuSkeleton />}>
            <OnboardingUserMenu />
          </Suspense>
        </div>
      )}
      <Outlet />
    </>
  )
}

const INTENT_EVENTS = ['pointerdown', 'keydown', 'input'] as const

/**
 * Every way out of these pages that is not a link lands in `_app` — a login, a sign-up, an accepted invitation — and a
 * `<Link>` preloads on intent by itself. So the signed-in shell's chunk starts on the visitor's first interaction, or
 * at once for one already signed in, and the navigation after submit has nothing to await.
 */
function usePreloadAppShell() {
  const router = useRouter()
  const queryClient = useQueryClient()
  const { sessionUserQueryOptions } = Route.useRouteContext()
  useEffect(() => {
    const preload = () => {
      router.loadRouteChunk(router.routesById['/_app'])?.catch(noop)
    }
    if (queryClient.getQueryData(sessionUserQueryOptions.queryKey)) {
      preload()
      return
    }
    const onIntent = () => {
      for (const type of INTENT_EVENTS) document.removeEventListener(type, onIntent, true)
      preload()
    }
    for (const type of INTENT_EVENTS) document.addEventListener(type, onIntent, { capture: true, passive: true })
    return () => {
      for (const type of INTENT_EVENTS) document.removeEventListener(type, onIntent, true)
    }
  }, [router, queryClient, sessionUserQueryOptions])
}
